import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { QuoteProvider } from "@/context/QuoteContext";
import Layout from "@/components/Layout";
import TitleManager from "@/components/TitleManager";

import HomePage from "@/pages/HomePage";
import CatalogPage from "@/pages/CatalogPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import SectorsPage from "@/pages/SectorsPage";
import UseCasesPage from "@/pages/UseCasesPage";
import ContentPage from "@/pages/ContentPage";
import QuoteRequestPage from "@/pages/QuoteRequestPage";
import AccountPage from "@/pages/AccountPage";
import { QuoteViewPage, TrackQuotePage } from "@/pages/QuoteViewPages";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/AuthPages";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import { AdminQuotes } from "@/pages/admin/AdminQuotes";
import AdminQuoteDetail from "@/pages/admin/AdminQuoteDetail";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminReview from "@/pages/admin/AdminReview";
import AdminLegal from "@/pages/admin/AdminLegal";
import AdminSeo from "@/pages/admin/AdminSeo";
import AdminGoogle from "@/pages/admin/AdminGoogle";
import AdminTaxonomy from "@/pages/admin/AdminTaxonomy";
import AdminProductForm from "@/pages/admin/AdminProductForm";
import AdminContent from "@/pages/admin/AdminContent";
import AdminRedirects from "@/pages/admin/AdminRedirects";
import AdminAudit from "@/pages/admin/AdminAudit";
import FaqPage from "@/pages/FaqPage";
import NotFoundPage from "@/pages/NotFoundPage";
import AdminAds from "@/pages/admin/AdminAds";
import GoogleCallback from "@/pages/admin/GoogleCallback";

function Protected({ children, role }) {
  const { user, checked } = useAuth();
  if (!checked) return <div className="min-h-screen flex items-center justify-center text-slate-400">Yükleniyor...</div>;
  if (!user) return <Navigate to="/giris" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

const Site = ({ children }) => <Layout>{children}</Layout>;

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <QuoteProvider>
        <BrowserRouter>
          <TitleManager />
          <Routes>
            <Route path="/" element={<Site><HomePage /></Site>} />
            <Route path="/urunler" element={<Site><CatalogPage /></Site>} />
            <Route path="/kategori/:slug" element={<Site><CatalogPage /></Site>} />
            <Route path="/urun/:urun_id" element={<Site><ProductDetailPage /></Site>} />
            <Route path="/sektorler" element={<Site><SectorsPage /></Site>} />
            <Route path="/kullanim-alanlari" element={<Site><UseCasesPage /></Site>} />
            <Route path="/hakkimizda" element={<Site><ContentPage slug="hakkimizda" /></Site>} />
            <Route path="/tedarik-sureci" element={<Site><ContentPage slug="tedarik-sureci" /></Site>} />
            <Route path="/iletisim" element={<Site><ContentPage slug="iletisim" /></Site>} />
            <Route path="/gizlilik" element={<Site><ContentPage slug="gizlilik" /></Site>} />
            <Route path="/aydinlatma" element={<Site><ContentPage slug="aydinlatma" /></Site>} />
            <Route path="/sss" element={<Site><FaqPage /></Site>} />
            <Route path="/teklif-iste" element={<Site><QuoteRequestPage /></Site>} />
            <Route path="/teklif-takip/:token" element={<Site><TrackQuotePage /></Site>} />

            <Route path="/giris" element={<LoginPage />} />
            <Route path="/kayit" element={<RegisterPage />} />
            <Route path="/sifremi-unuttum" element={<ForgotPasswordPage />} />
            <Route path="/sifre-sifirla" element={<ResetPasswordPage />} />

            <Route path="/hesabim" element={<Protected><Site><AccountPage /></Site></Protected>} />
            <Route path="/teklif/:quote_no" element={<Protected><Site><QuoteViewPage /></Site></Protected>} />

            <Route path="/yonetim" element={<Protected role="admin"><AdminLayout /></Protected>}>
              <Route index element={<AdminDashboard />} />
              <Route path="urunler" element={<AdminProducts />} />
              <Route path="urunler/yeni" element={<AdminProductForm />} />
              <Route path="urunler/:urun_id" element={<AdminProductForm />} />
              <Route path="kategoriler" element={<AdminTaxonomy kind="kategoriler" key="kat" />} />
              <Route path="sektorler" element={<AdminTaxonomy kind="sektorler" key="sek" />} />
              <Route path="kullanim-alanlari" element={<AdminTaxonomy kind="kullanim-alanlari" key="kul" />} />
              <Route path="markalar" element={<AdminTaxonomy kind="markalar" key="mar" />} />
              <Route path="tedarikciler" element={<AdminTaxonomy kind="tedarikciler" key="ted" />} />
              <Route path="icerik" element={<AdminContent />} />
              <Route path="yonlendirmeler" element={<AdminRedirects />} />
              <Route path="degisiklik-gecmisi" element={<AdminAudit />} />
              <Route path="teklifler" element={<AdminQuotes />} />
              <Route path="teklifler/:quote_no" element={<AdminQuoteDetail />} />
              <Route path="ayarlar" element={<AdminSettings />} />
              <Route path="kontrol" element={<AdminReview />} />
              <Route path="yasal-metinler" element={<AdminLegal />} />
              <Route path="seo" element={<AdminSeo />} />
              <Route path="search-console" element={<AdminGoogle />} />
              <Route path="google-ads" element={<AdminAds />} />
              <Route path="google/baglan" element={<GoogleCallback />} />
            </Route>

            {/* Bilinmeyen/eski URL'ler (molarkimya.com geçişinde eski linkler) 404 yerine anasayfaya döner */}
            <Route path="*" element={<Site><NotFoundPage /></Site>} />
          </Routes>
        </BrowserRouter>
      </QuoteProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
